import { mdiArrowRight } from '@mdi/js';
import Icon from '@mdi/react';
import get from 'lodash/get';
import React, { useEffect, useRef, useState } from 'react';
import { isMobileOnly as isMobile } from 'react-device-detect';
import { useInView } from 'react-intersection-observer';
import {
  getUserStatus,
  makeApiCall,
  processApiValues,
  isWithinTradingHours,
} from '../../../helpers/utils';
import { connect, useSelector } from 'react-redux';
import { loadMoreInteraction } from '../../../helpers/datalayer-utils';
import { BQLink } from '../../atoms/bqlink';
import BudgetStocksInFocusHorizontalShimmer from '../../shimmers/budget-stocks-in-focus-horizontal-shimmer';
import { useDarkModeFromQuery, useInterval } from '../../../helpers/custom-hooks';
import './budget-stocks-in-focus-horizontal.m.css';

const BudgetStocksInFocusHorizontal = ({
  widget = false,
  pageType,
  limit = '6',
  customClass = '',
  darkMode = '',
}) => {
  const isDarkMode = darkMode === 'false' ? false : useDarkModeFromQuery();
  const [currentStock, setCurrentStock] = useState('NSE');
  const [currentTab, setCurrentTab] = useState('most_active');
  const [currentStockValues, setCurrentStockValues] = useState([]);
  const [initialCall, setInitialCall] = useState(true);
  const isFirstRender = useRef(true);
  const userData = useSelector((state) => get(state, ['userData']));
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );
  const [ref, inView] = useInView();

  const getStockFocusData = async (stock) => {
    const url = `/foapi/market/getStockInsight?limit=${isMobile ? '6' : limit}`;
    const mapping = {
      NSE: 'NIFTY%20500',
      BSE: 'BSE%20-%20500',
    };

    try {
      const res1 = await makeApiCall(url + '&group=' + mapping[stock] + '&type=0');
      const res2 = await makeApiCall(url + '&group=' + mapping[stock] + '&type=1');
      const res3 = await makeApiCall(url + '&group=' + mapping[stock] + '&type=2');
      setCurrentStockValues(processApiValues([res1, res2, res3]));
    } catch (error) {
      setCurrentStockValues([]);
    }
  };

  const takeToStockPage = (stock) => {
    makeApiCall(`/api/v1/entities?type=stock&q=${stock}&limit=10`).then((res) => {
      if (
        res.entities &&
        res.entities.length > 0 &&
        res.entities.find((ent) => ent['nse-code'] === stock)
      ) {
        window.open(
          `/stock/${res.entities.find((ent) => ent['nse-code'] === stock)['stock-id']}/${res.entities.find((ent) => ent['nse-code'] === stock).slug.replace('-stock', '')}`,
          '_blank',
        );
      }
    });
  };

  const activeColumnTitle = () => {
    const imgSrc = currentTab === 'most_active' ? 'fire_icon.svg' : 'fire_icon_grey.svg';
    return (
      <div
        styleName={`title-ctx ${currentTab === 'most_active' ? 'active' : ''}`}
        onClick={() => setCurrentTab('most_active')}
      >
        <img styleName="image" src={`/icons/${imgSrc}`} alt="Fire Icon" />
        <span styleName="title">Most Active</span>
      </div>
    );
  };

  const gainerColumnTitle = () => {
    const imgSrc =
      currentTab === 'gainers' ? 'arrow-top-right-thick.svg' : 'arrow-top-right-thick-grey.svg';
    return (
      <div
        styleName={`title-ctx custom ${currentTab === 'gainers' ? 'active' : ''}`}
        onClick={() => setCurrentTab('gainers')}
      >
        <img styleName="image" src={`/icons/${imgSrc}`} alt="Gainers Icon" />
        <span styleName="title">Gainers</span>
      </div>
    );
  };

  const loserColumnTitle = () => {
    const imgSrc =
      currentTab === 'losers'
        ? 'arrow-bottom-right-thick.svg'
        : 'arrow-bottom-right-thick-grey.svg';
    return (
      <div
        styleName={`title-ctx custom ${currentTab === 'losers' ? 'active' : ''}`}
        onClick={() => setCurrentTab('losers')}
      >
        <img styleName="image" src={`/icons/${imgSrc}`} alt="Losers Icon" />
        <span styleName="title">Losers</span>
      </div>
    );
  };

  const allTitles = () => {
    return (
      <React.Fragment>
        {activeColumnTitle()}
        {gainerColumnTitle()}
        {loserColumnTitle()}
      </React.Fragment>
    );
  };

  const updateStock = (stock) => {
    setCurrentStock(stock);
    getStockFocusData(stock);
  };
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      const baseData = {
        event: 'tab_interaction',
        cta_text: currentTab,
        section_name: 'stocks in focus',
        page_template: pageType || 'NA',
        user_id: userData?.userProfile?.userId || 'NA',
        user_status: getUserStatus(),
        login_type: userData?.isLoggedIn ? 'logged-in' : 'logged-out',
        language_hit: 'english',
        Platform: isMobile ? 'mobile' : 'desktop',
        SiteName: 'www.ndtvprofit.com',
      };

      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push(baseData);
      } else {
        console.error('Error: window.dataLayer is not available');
      }
    }
  }, [currentTab]);
  useEffect(() => {
    if (initialCall && inView) {
      getStockFocusData(currentStock);
      setInitialCall(false);
    }
  }, [inView]);
  useInterval(() => {
    if (isWithinTradingHours(tradingHolidays, specialTradingDays) && inView) {
      getStockFocusData(currentStock);
    }
  }, 10000);

  const recordIndex = currentTab === 'gainers' ? 1 : currentTab === 'losers' ? 0 : 2;

  return (
    <div
      ref={ref}
      className={!widget ? 'container' : ''}
      styleName={`budget-stock-horizontal ${widget ? 'widget-view ' + customClass : ''}`}
    >
      <div styleName="stock-wrap">
        <div styleName="top-slider">
          <div styleName="all-titles">{allTitles()}</div>
          <div styleName="toggles">
            <div
              styleName={`toggle ${currentStock === 'NSE' ? 'active' : ''}`}
              onClick={() => updateStock('NSE')}
            >
              NSE
            </div>
            <div
              styleName={`toggle ${currentStock === 'BSE' ? 'active' : ''}`}
              onClick={() => updateStock('BSE')}
            >
              BSE
            </div>
          </div>
        </div>
        {currentStockValues?.length > 0 ? (
          <>
            <div styleName="stock-list">
              {currentStockValues &&
                currentStockValues[recordIndex]?.map((val, index) => (
                  <div
                    styleName={`stocks ${val.change === 0 || val.change > 0 ? 'stocks-bg' : ''} ${isDarkMode ? 'dark' : ''}`}
                    onClick={() => takeToStockPage(val.exchangeSymbol)}
                    key={index}
                  >
                    <div
                      styleName={`stock-img ${
                        val.change === 0 || val.change > 0 ? 'stock-img-bg' : ''
                      }`}
                    >
                      <img
                        styleName="image"
                        src={`/icons/${
                          val.change === 0 || val.change > 0
                            ? 'arrow-top-right-thick.svg'
                            : 'arrow-bottom-right-thick.svg'
                        }`}
                        alt="Losers Icon"
                      />
                    </div>

                    <div styleName={`stock-detail ${isDarkMode ? 'dark' : ''}`}>
                      <span styleName="name">{val.name}</span>
                      <span styleName="amt">{val.lastPrice}</span>
                      <span styleName={`avg ${val.change === 0 || val.change > 0 ? 'up-avg' : ''}`}>
                        {Number(val.change).toFixed(2)} ({val.changePer}%)
                      </span>
                    </div>
                  </div>
                ))}
            </div>
            <BQLink styleName="more-btn" href="/markets" className="container">
              <div
                styleName="btn"
                onClick={() =>
                  loadMoreInteraction('More from Markets', userData, pageType, 'stocks in focus')
                }
              >
                More from Markets &nbsp;
                <Icon path={mdiArrowRight} size="20px" color="#2b2b2b" />
              </div>
            </BQLink>
          </>
        ) : (
          <BudgetStocksInFocusHorizontalShimmer />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  pageType: get(state, ['qt', 'pageType'], ''),
});

export default connect(mapStateToProps)(BudgetStocksInFocusHorizontal);
